import { Stack, IconButton, Sheet, Box, Typography, Link } from '@mui/joy';
import { Drawer } from '@mui/material';
import { type ReactElement, Suspense } from 'react';
import { AutoAwesome } from '@mui/icons-material';

import { REPLAY_UNMASK_CLASS_NAME } from 'setup/initializeAll.ts';
import { useCreateMenu as useMenu, footerMenu } from './Menu';
import { SidebarMenu } from './SidebarMenu';
import { SidebarTop } from './SidebarTop';
import { useScreenBreakpointDown } from '../screenSizeUtils';
import { KeyboardArrowRight, KeyboardArrowLeft, Menu } from '@mui/icons-material';
import Loader from '../Loader/Loader.tsx';
import { getEnvType } from 'envConfig.ts';
import { useFlag } from '@unleash/proxy-client-react';

type SidebarProps = {
  setSideBarOpen: (isOpen: boolean) => void;
  isSideBarOpen: boolean;
};

export const sidebarAnimation = '225ms cubic-bezier(0.0, 0, 0.2, 1) 0ms';

const Sidebar = ({ setSideBarOpen, isSideBarOpen }: SidebarProps): ReactElement => {
  const toggleSidebar = (): void => setSideBarOpen(!isSideBarOpen);
  const mobileView = useScreenBreakpointDown('sm');

  return (
    <>
      <IconButton
        size={mobileView ? 'lg' : 'sm'}
        onClick={toggleSidebar}
        sx={(theme) => ({
          // mobile: the toggle button is at the bottom and when menu collapsed it's "floating" not half hidden
          // desktop: it is at the top and when sidebar collapsed it's half hidden
          bottom: { xs: 10, sm: 'unset' },
          top: { sm: 12 },
          transform: { xs: isSideBarOpen ? 'translateX(-50%)' : undefined, sm: 'translateX(-50%)' },

          position: 'fixed',
          borderRadius: '50%',
          transition: isSideBarOpen ? `left ${sidebarAnimation}` : undefined,
          left: isSideBarOpen ? 'var(--sidebar-width)' : 2,
          zIndex: theme.zIndex.modal - 10,
          backgroundColor: theme.palette.background.surface,
          border: '1px solid',
          borderColor: theme.palette.divider,
          '&:hover': {
            backgroundColor: theme.palette.primary.plainHoverBg,
          },
        })}
      >
        {isSideBarOpen ? (
          <KeyboardArrowLeft />
        ) : mobileView ? (
          // menu looks better than an arrow as a floating button
          <Menu />
        ) : (
          // IconButton is half hidden behind left edge of the screen
          // to not cut the arrow itself we move it 3px to the right from the center of IconButton
          <KeyboardArrowRight sx={{ transform: 'translateX(3px)' }} />
        )}
      </IconButton>
      {/* for now classic mui Drawer is used, mui joy does not support 'persistent' drawer, they have only plan for analog navigation sidebar component */}
      <Drawer
        variant={mobileView ? 'temporary' : 'persistent'}
        open={isSideBarOpen}
        className={REPLAY_UNMASK_CLASS_NAME}
        anchor="left"
        PaperProps={{
          sx: {
            width: 'var(--sidebar-width)',
            backgroundColor: 'var(--joy-palette-background-surface)',
            boxSizing: 'border-box',
          },
        }}
        sx={{ flexShrink: 0 }}
      >
        <SidebarTop />
        <Stack justifyContent="space-between" flexGrow={1} spacing={4} minHeight={0}>
          <Suspense fallback={<Loader appearance="container" />}>
            <SidebarMenuSection />
          </Suspense>
        </Stack>
      </Drawer>
    </>
  );
};

const SidebarMenuSection = () => {
  const menu = useMenu();
  return (
    <Stack justifyContent="space-between" flexGrow={1} spacing={4} minHeight={0}>
      <SidebarMenu menu={menu} scrollable />
      <NewExperienceBanner />
      <SidebarMenu menu={footerMenu} />
    </Stack>
  );
};

const NewExperienceBanner = () => {
  const isEnabled = useFlag('pms.v1.new-experience-banner');
  const env = getEnvType();
  const url =
    env === 'prod' || env === 'localhost-prod'
      ? 'https://pms.genieai.tech'
      : env === 'dev'
        ? 'https://pms.genieai.fr'
        : 'http://localhost:3003';

  if (!isEnabled) return <></>;

  return (
    <Sheet sx={{ flexShrink: 0, paddingInline: 2, paddingBlock: 1 }}>
      <Box
        sx={(theme) => ({
          background: `linear-gradient(135deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
          borderRadius: 'md',
          padding: 2.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          boxShadow: 'sm',
        })}
      >
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'start', gap: 1 }}>
          <AutoAwesome sx={{ fontSize: 20, color: 'white' }} />
          <Typography
            level="title-md"
            sx={{
              color: 'white',
              fontWeight: 'bold',
              lineHeight: 1.3,
            }}
          >
            New Experience
          </Typography>
        </Box>

        <Typography
          level="body-sm"
          sx={{
            color: 'rgba(255, 255, 255, 0.9)',
            lineHeight: 1.5,
          }}
        >
          Try our redesigned interface with enhanced features and better performance.
        </Typography>

        <Link
          href={url}
          target="_blank"
          underline="none"
          sx={(theme) => ({
            backgroundColor: 'white',
            color: theme.palette.primary[600],
            padding: '12px 16px',
            borderRadius: 'sm',
            textAlign: 'center',
            fontWeight: 'bold',
            fontSize: '0.9375rem',
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            minHeight: '44px',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
            },
          })}
        >
          Try Now →
        </Link>
      </Box>
    </Sheet>
  );
};

export default Sidebar;
